import React, { useState, useCallback, useRef, useEffect } from "react";
import { MoveFocusInside } from "react-focus-lock";
import { useDispatch, useSelector } from "react-redux";
import { Paper, ResizeTransition, SlideLeftRightTransition } from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { fetchProjectsDetails } from "~/store/actions/actions.projects";
import { getConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { ProjectList } from "./ProjectList";
import { TaskList } from "./TaskList";
import useClickEvent from "../Hooks/useClickEvent.jsx";

const ProjectTaskMenuSelector = ({ className, onProjectSelect, onTaskSelect, onClose, onBack, width }) => {
  const ref = useRef(null);
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [picker, setPicker] = useState("projects");
  const [isLeft, setIsLeft] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState();
  const [selectedTaskId, setSelectedTaskId] = useState();
  const [height, setHeight] = useState(72);
  const taskTimeTrackingEnabled = useSelector((state) => getConfigOption(state, "task_time_tracking_enabled"));

  useEffect(() => {
    dispatch(fetchProjectsDetails());
  }, [dispatch]);

  const handleBack = useCallback(() => {
    setSelectedProjectId(undefined);
    setSelectedTaskId(undefined);
    setStep(1);
    setIsLeft(false);
    typeof onBack === "function" && onBack();
  }, [onBack]);

  const handleSetProject = useCallback(
    (id) => {
      setSelectedProjectId(id);
      setIsLeft(true);
      if (taskTimeTrackingEnabled) {
        setStep(2);
      }

      onProjectSelect(id);
    },
    [taskTimeTrackingEnabled, onProjectSelect]
  );

  const handleSetTask = useCallback(
    (id) => {
      setSelectedTaskId(id);
      onTaskSelect(id);
    },
    [setSelectedTaskId, onTaskSelect]
  );

  const handleCloseSelector = useCallback(
    (e) => {
      if (ref && e && !ref.current?.contains(e.target)) {
        onClose(e);
      }
    },
    [ref, onClose]
  );

  useClickEvent(handleCloseSelector);

  const handleHeight = useCallback((element) => {
    setHeight(element?.offsetHeight ? element.offsetHeight : 0);
  }, []);

  return (
    <Paper
      type="paper-2"
      className={classnames("project_task_menu_selector tw-absolute tw-overflow-hidden", className)}
    >
      <ResizeTransition in>
        <div style={{ height, width }}>
          <SlideLeftRightTransition
            in={step === 1}
            direction={isLeft ? "left" : "right"}
            onEnter={handleHeight}
            onEntered={() => setPicker("projects")}
          >
            <MoveFocusInside disabled={picker !== "projects"}>
              <ProjectList
                focusInput={picker === "projects"}
                onClick={handleSetProject}
                onHeightChange={setHeight}
                style={{ width }}
                onlyWithTrackingEnabled
              />
            </MoveFocusInside>
          </SlideLeftRightTransition>
          <SlideLeftRightTransition
            in={step === 2}
            direction={isLeft ? "left" : "right"}
            onEnter={handleHeight}
            onEntered={() => setPicker("tasks")}
          >
            <MoveFocusInside disabled={picker !== "tasks"}>
              <TaskList
                focusInput={picker === "tasks"}
                onBack={handleBack}
                projectId={selectedProjectId}
                selectedId={selectedTaskId}
                onChange={handleSetTask}
                onHeightChange={setHeight}
                style={{ width }}
                changeHeight={step === 2}
              />
            </MoveFocusInside>
          </SlideLeftRightTransition>
        </div>
      </ResizeTransition>
    </Paper>
  );
};

ProjectTaskMenuSelector.propTypes = {
  className: PropTypes.string,
  onProjectSelect: PropTypes.func,
  onTaskSelect: PropTypes.func,
  onClose: PropTypes.func,
  onBack: PropTypes.func,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ProjectTaskMenuSelector.defaultProps = {
  onProjectSelect: () => null,
  onTaskSelect: () => null,
  onClose: () => null,
  width: 350,
};

export default ProjectTaskMenuSelector;
